import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Save, MessageSquare, Plus, Trash2, X,
} from 'lucide-react';
import { toast as sonner } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { key: 'dispatcher', label: 'Dispatchers' },
  { key: 'company_admin', label: 'Company Admins' },
  { key: 'driver', label: 'Drivers' },
];

const ChatDepartmentsConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // dept key currently picking members for

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/direct-chat/departments/admin`, { withCredentials: true });
        setDepts(res.data.departments || []);
        setUsers(res.data.company_users || []);
      } catch (e) {
        sonner.error(e.response?.data?.detail || 'Failed to load departments');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateDept = (key, patch) => {
    setDepts((prev) => prev.map((d) => d.key === key ? { ...d, ...patch } : d));
    setDirty(true);
  };

  const toggleRole = (key, role) => {
    setDepts((prev) => prev.map((d) => {
      if (d.key !== key) return d;
      const roles = d.roles.includes(role) ? d.roles.filter((r) => r !== role) : [...d.roles, role];
      return { ...d, roles };
    }));
    setDirty(true);
  };

  const toggleMember = (key, userId) => {
    setDepts((prev) => prev.map((d) => {
      if (d.key !== key) return d;
      const ids = d.member_user_ids.includes(userId)
        ? d.member_user_ids.filter((x) => x !== userId)
        : [...d.member_user_ids, userId];
      return { ...d, member_user_ids: ids };
    }));
    setDirty(true);
  };

  const removeDept = (key) => {
    if (!window.confirm('Remove this department?')) return;
    setDepts((prev) => prev.filter((d) => d.key !== key));
    setDirty(true);
  };

  const addDept = () => {
    const label = window.prompt('Department name (e.g., "Recruiting")');
    if (!label || !label.trim()) return;
    const cleanLabel = label.trim();
    const key = cleanLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32);
    if (depts.some((d) => d.key === key)) {
      sonner.error('A department with that name already exists');
      return;
    }
    setDepts((prev) => [...prev, {
      key, label: cleanLabel, roles: ['company_admin'], member_user_ids: [], enabled: true,
    }]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        departments: depts.map((d) => ({
          key: d.key,
          label: d.label,
          roles: d.roles,
          member_user_ids: d.member_user_ids,
          enabled: d.enabled,
        })),
      };
      await axios.put(`${API}/direct-chat/departments`, payload, { withCredentials: true });
      sonner.success('Chat departments saved');
      setDirty(false);
    } catch (e) {
      sonner.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6 flex items-center justify-center text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading chat departments…
      </div>
    );
  }

  return (
    <div data-testid="chat-departments-config" className="space-y-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <MessageSquare className="w-5 h-5 text-[#002FA7]" />
            Team Chat Departments
          </h2>
          <p className="text-xs text-slate-500">
            Departments that appear when someone clicks "New chat". Pick which roles auto-belong, or hand-pick specific people.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addDept}
            data-testid="chat-dept-add"
            className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 px-2.5 py-1.5 rounded hover:bg-slate-50"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="chat-dept-save"
            className="inline-flex items-center gap-1.5 bg-[#002FA7] text-white px-3 py-1.5 text-xs rounded-md font-semibold hover:bg-[#00247D] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
        </div>
      </div>

      {depts.map((d) => (
        <div
          key={d.key}
          data-testid={`chat-dept-${d.key}`}
          className={`border rounded-md p-4 transition-colors ${d.enabled ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <input
                  value={d.label}
                  onChange={(e) => updateDept(d.key, { label: e.target.value })}
                  data-testid={`chat-dept-${d.key}-label`}
                  className="font-semibold text-slate-900 bg-transparent border-b border-transparent focus:border-[#002FA7] focus:outline-none px-1 py-0.5"
                />
                <span className="text-[10px] text-slate-400 font-mono">[{d.key}]</span>
                {d.member_count !== undefined && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 bg-slate-100 rounded">
                    {d.member_count} {d.member_count === 1 ? 'member' : 'members'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid={`chat-dept-${d.key}-enabled`}>
                <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${d.enabled ? 'bg-[#34C759]' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${d.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </span>
                <input type="checkbox" className="sr-only" checked={d.enabled} onChange={() => updateDept(d.key, { enabled: !d.enabled })} />
                <span className="font-medium text-slate-600">{d.enabled ? 'On' : 'Off'}</span>
              </label>
              <button
                onClick={() => removeDept(d.key)}
                data-testid={`chat-dept-${d.key}-remove`}
                className="p-1 text-slate-400 hover:text-red-600"
                title="Remove department"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Auto-include roles</p>
              <div className="flex flex-wrap gap-1.5">
                {ROLE_OPTIONS.map((r) => {
                  const active = d.roles.includes(r.key);
                  return (
                    <button
                      key={r.key}
                      data-testid={`chat-dept-${d.key}-role-${r.key}`}
                      onClick={() => toggleRole(d.key, r.key)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${active ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 flex items-center justify-between">
                <span>Specific members ({d.member_user_ids.length})</span>
                <button
                  onClick={() => setPickerFor(pickerFor === d.key ? null : d.key)}
                  data-testid={`chat-dept-${d.key}-pick`}
                  className="text-[#002FA7] hover:underline normal-case tracking-normal text-xs font-semibold"
                >
                  {pickerFor === d.key ? 'Close' : 'Add/Remove'}
                </button>
              </p>
              {pickerFor === d.key ? (
                <div className="border border-slate-200 rounded p-2 max-h-44 overflow-y-auto bg-slate-50">
                  {users.length === 0 ? (
                    <p className="text-xs text-slate-500">No users in company.</p>
                  ) : (
                    users.map((u) => {
                      const checked = d.member_user_ids.includes(u.id);
                      return (
                        <label
                          key={u.id}
                          className={`flex items-center gap-2 px-2 py-1 text-xs rounded cursor-pointer ${checked ? 'bg-[#002FA7]/10' : 'hover:bg-white'}`}
                          data-testid={`chat-dept-${d.key}-user-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleMember(d.key, u.id)}
                            className="accent-[#002FA7]"
                          />
                          <span className="flex-1 truncate">{u.name || u.email}</span>
                          <span className="text-[10px] uppercase text-slate-400">{u.role}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              ) : d.member_user_ids.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {d.member_user_ids.map((uid) => {
                    const u = users.find((x) => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-slate-100 rounded-full">
                        {u.name || u.email}
                        <button
                          onClick={() => toggleMember(d.key, uid)}
                          className="text-slate-400 hover:text-red-600"
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400">None — using role auto-include only.</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChatDepartmentsConfig;
